import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  ArrowLeft, Upload, FileSpreadsheet, Download, CheckCircle, XCircle,
  AlertCircle, Loader2, Users, Building, RefreshCw, Trash2
} from "lucide-react";

const BulkBookingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [corporateClients, setCorporateClients] = useState([]);
  const [selectedCorporate, setSelectedCorporate] = useState("");
  const [sendConfirmations, setSendConfirmations] = useState(true);
  const [parsedData, setParsedData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [result, setResult] = useState(null);
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    fetchCorporateClients();
    fetchTemplate();
  }, []);

  const fetchCorporateClients = async () => {
    try {
      const response = await api.get("/corporate-clients");
      setCorporateClients(response.data);
    } catch (error) {
      console.error("Failed to load corporate clients");
    }
  };

  const fetchTemplate = async () => {
    try {
      const response = await api.get("/bookings/bulk/template");
      setTemplate(response.data);
    } catch (error) {
      console.error("Failed to load template");
    }
  };

  const downloadTemplate = () => {
    if (!template) return;
    
    const blob = new Blob([template.sample_csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk_booking_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Template downloaded");
  };

  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) {
      throw new Error("CSV must have a header row and at least one data row");
    }

    const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/['"]/g, ""));
    const data = [];
    const errors = [];

    // Required columns
    const requiredColumns = ["passenger_name", "passenger_email", "passenger_phone", "pickup_location", "pickup_date", "pickup_time"];
    const missingColumns = requiredColumns.filter(col => !headers.includes(col));
    
    if (missingColumns.length > 0) {
      throw new Error(`Missing required columns: ${missingColumns.join(", ")}`);
    }

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Parse CSV line (handle quoted values)
      const values = [];
      let current = "";
      let inQuotes = false;
      
      for (let char of line) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === "," && !inQuotes) {
          values.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const row = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx] || "";
      });

      // Validate row
      const rowErrors = [];
      if (!row.passenger_name) rowErrors.push("Missing passenger name");
      if (!row.passenger_email) rowErrors.push("Missing email");
      if (!row.passenger_phone) rowErrors.push("Missing phone");
      if (!row.pickup_location) rowErrors.push("Missing pickup location");
      if (!row.pickup_date) rowErrors.push("Missing pickup date");
      if (!row.pickup_time) rowErrors.push("Missing pickup time");
      
      // Validate email format
      if (row.passenger_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.passenger_email)) {
        rowErrors.push("Invalid email format");
      }
      
      // Validate date format
      if (row.pickup_date && !/^\d{4}-\d{2}-\d{2}$/.test(row.pickup_date)) {
        rowErrors.push("Invalid date format (use YYYY-MM-DD)");
      }
      
      // Validate time format
      if (row.pickup_time && !/^\d{2}:\d{2}$/.test(row.pickup_time)) {
        rowErrors.push("Invalid time format (use HH:MM)");
      }

      data.push({
        ...row,
        rowNumber: i,
        isValid: rowErrors.length === 0,
        errors: rowErrors
      });

      if (rowErrors.length > 0) {
        errors.push({ row: i, errors: rowErrors });
      }
    }

    return { data, errors };
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      toast.error("Please upload a CSV file");
      return;
    }

    setUploading(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const { data, errors } = parseCSV(e.target.result);
        setParsedData(data);
        setValidationErrors(errors);
        setResult(null);
        
        if (errors.length > 0) {
          toast.warning(`${errors.length} rows have validation errors`);
        } else {
          toast.success(`${data.length} rows parsed successfully`);
        }
      } catch (error) {
        toast.error(error.message);
        setParsedData([]);
        setValidationErrors([]);
      } finally {
        setUploading(false);
      }
    };

    reader.onerror = () => {
      toast.error("Failed to read file");
      setUploading(false);
    };

    reader.readAsText(file);
  };

  const handleSubmit = async () => {
    const validRows = parsedData.filter(row => row.isValid);
    
    if (validRows.length === 0) {
      toast.error("No valid rows to submit");
      return;
    }

    setLoading(true);

    try {
      const bookings = validRows.map(row => ({
        passenger_name: row.passenger_name,
        passenger_email: row.passenger_email,
        passenger_phone: row.passenger_phone,
        pickup_location: row.pickup_location,
        dropoff_location: row.dropoff_location || null,
        pickup_date: row.pickup_date,
        pickup_time: row.pickup_time,
        vehicle_type: row.vehicle_type || "sedan",
        num_passengers: parseInt(row.num_passengers) || 1,
        flight_number: row.flight_number || null,
        special_requirements: row.special_requirements || null,
        price: row.price ? parseFloat(row.price) : null
      }));

      const response = await api.post("/bookings/bulk", {
        corporate_client_id: selectedCorporate || null,
        bookings,
        send_confirmations: sendConfirmations
      });

      setResult(response.data);
      toast.success(`Created ${response.data.successful} bookings!`);

    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to create bookings";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const clearData = () => {
    setParsedData([]);
    setValidationErrors([]);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validCount = parsedData.filter(r => r.isValid).length;
  const invalidCount = parsedData.filter(r => !r.isValid).length;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass sticky top-0 z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Bulk Booking Import</h1>
              <p className="text-sm text-muted-foreground">Upload CSV to create multiple bookings</p>
            </div>
          </div>
          <div className="flex gap-2">
            {parsedData.length > 0 && (
              <Button variant="outline" onClick={clearData} data-testid="clear-btn">
                <Trash2 className="h-4 w-4 mr-2" />
                Clear
              </Button>
            )}
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Result Summary */}
        {result && (
          <Card className="dashboard-card mb-6 border-green-500/30" data-testid="result-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Import Complete!</h2>
                  <p className="text-muted-foreground">
                    {result.successful} of {result.total} bookings created successfully
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-4 rounded-lg bg-green-500/10 text-center">
                  <p className="text-3xl font-bold text-green-500">{result.successful}</p>
                  <p className="text-sm text-muted-foreground">Successful</p>
                </div>
                <div className="p-4 rounded-lg bg-red-500/10 text-center">
                  <p className="text-3xl font-bold text-red-500">{result.failed}</p>
                  <p className="text-sm text-muted-foreground">Failed</p>
                </div>
                <div className="p-4 rounded-lg bg-black/5 text-center">
                  <p className="text-3xl font-bold">{result.total}</p>
                  <p className="text-sm text-muted-foreground">Total</p>
                </div>
              </div>

              {result.created_bookings.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-medium mb-2">Created Bookings:</h3>
                  <div className="max-h-[200px] overflow-y-auto">
                    <table className="data-table text-sm">
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Reference</th>
                          <th>Passenger</th>
                          <th>Date/Time</th>
                          <th>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.created_bookings.map((b, i) => (
                          <tr key={i}>
                            <td>{b.row}</td>
                            <td className="font-mono text-primary">{b.booking_ref}</td>
                            <td>{b.passenger_name}</td>
                            <td>{new Date(b.pickup_datetime).toLocaleString()}</td>
                            <td className="text-green-500">${b.price?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {result.errors.length > 0 && (
                <div className="mt-4 p-4 rounded-lg bg-red-500/10">
                  <h3 className="font-medium text-red-500 mb-2">Errors:</h3>
                  {result.errors.map((err, i) => (
                    <p key={i} className="text-sm text-red-400">
                      Row {err.row}: {err.error}
                    </p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upload Section */}
          <div className="lg:col-span-1 space-y-6">
            {/* File Upload */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5 text-primary" />
                  Upload CSV
                </CardTitle>
                <CardDescription>Select a CSV file with booking data</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div
                  className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-area"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  {uploading ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Processing...</span>
                    </div>
                  ) : (
                    <>
                      <p className="font-medium">Click to upload CSV</p>
                      <p className="text-sm text-muted-foreground mt-1">or drag and drop</p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Options */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle>Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Corporate Account</Label>
                  <Select value={selectedCorporate || "none"} onValueChange={(v) => setSelectedCorporate(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="corporate-select">
                      <SelectValue placeholder="Select corporate account" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No corporate account</SelectItem>
                      {corporateClients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4" />
                            {client.company_name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Send Confirmation Emails</Label>
                    <p className="text-xs text-muted-foreground">Email each passenger after booking</p>
                  </div>
                  <Switch
                    checked={sendConfirmations}
                    onCheckedChange={setSendConfirmations}
                    data-testid="send-confirmations-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Template Info */}
            {template && (
              <Card className="dashboard-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="h-5 w-5 text-primary" />
                    CSV Format
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {template.columns.map((col, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Badge variant={col.required ? "default" : "secondary"} className="text-xs mt-0.5">
                          {col.required ? "Required" : "Optional"}
                        </Badge>
                        <div>
                          <p className="font-mono text-primary">{col.name}</p>
                          <p className="text-muted-foreground text-xs">{col.example}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-2">
            <Card className="dashboard-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-primary" />
                      Data Preview
                    </CardTitle>
                    <CardDescription>
                      {parsedData.length > 0 
                        ? `${parsedData.length} rows loaded`
                        : "Upload a CSV file to preview data"
                      }
                    </CardDescription>
                  </div>
                  {parsedData.length > 0 && (
                    <div className="flex items-center gap-3">
                      <Badge variant="default" className="bg-green-500/20 text-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {validCount} Valid
                      </Badge>
                      {invalidCount > 0 && (
                        <Badge variant="destructive" className="bg-red-500/20 text-red-500">
                          <XCircle className="h-3 w-3 mr-1" />
                          {invalidCount} Invalid
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {parsedData.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileSpreadsheet className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p>No data loaded</p>
                    <p className="text-sm mt-2">Upload a CSV file to see preview</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto max-h-[500px]">
                    <table className="data-table text-sm">
                      <thead className="sticky top-0 bg-card">
                        <tr>
                          <th>Row</th>
                          <th>Status</th>
                          <th>Passenger</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Pickup</th>
                          <th>Dropoff</th>
                          <th>Date</th>
                          <th>Time</th>
                          <th>Vehicle</th>
                          <th>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedData.map((row, i) => (
                          <tr key={i} className={!row.isValid ? "bg-red-500/10" : ""}>
                            <td>{row.rowNumber}</td>
                            <td>
                              {row.isValid ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              ) : (
                                <div className="flex items-center gap-1">
                                  <AlertCircle className="h-4 w-4 text-red-500" />
                                  <span className="text-xs text-red-500">{row.errors.join(", ")}</span>
                                </div>
                              )}
                            </td>
                            <td className="font-medium">{row.passenger_name}</td>
                            <td>{row.passenger_email}</td>
                            <td>{row.passenger_phone}</td>
                            <td className="max-w-[150px] truncate">{row.pickup_location}</td>
                            <td className="max-w-[150px] truncate">{row.dropoff_location || "-"}</td>
                            <td>{row.pickup_date}</td>
                            <td>{row.pickup_time}</td>
                            <td className="capitalize">{row.vehicle_type || "sedan"}</td>
                            <td>{row.price ? `$${parseFloat(row.price).toFixed(2)}` : "Auto"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {parsedData.length > 0 && validCount > 0 && (
                  <div className="mt-6 flex items-center justify-between p-4 rounded-lg bg-primary/10 border border-primary/20">
                    <div>
                      <p className="font-medium">Ready to import {validCount} bookings</p>
                      <p className="text-sm text-muted-foreground">
                        {sendConfirmations 
                          ? "Confirmation emails will be sent to each passenger"
                          : "No confirmation emails will be sent"
                        }
                      </p>
                    </div>
                    <Button 
                      onClick={handleSubmit} 
                      disabled={loading}
                      size="lg"
                      data-testid="submit-bulk-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Creating Bookings...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Create {validCount} Bookings
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BulkBookingPage;
